import { useMemo, FC, useCallback } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { updateImageUrl } from 'utils/convertString';
import { ICataloguesProduct } from 'stores/brands/types';
import { Table } from 'components/Core/Table';
import { AvatarIcon } from 'components/Core/AvatarIcon/AvatarIcon';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { CataloguesProductPositionCell } from '../CataloguesProductPositionCell/CataloguesProductPositionCell';
import { ReactComponent as CloseIcon } from 'assets/delete_icon_red.svg';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { CatalogueProductsTableTransFields } from './types';
import { catalogueProductsTableTranslations } from './translations';
import styles from './catalogueProductsTable.module.scss';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { IProductData } from 'stores/products/types';
import { DEFAULT_PRODUCT_POSITION } from 'components/BrandComponents/BrandCataloguesPage/constants';
import { useThrottleFn } from 'hooks/useThrottleFn';

const AVATAR_SIZE = 56;

export const CatalogueProductsTable: FC = observer(() => {
  const {
    popUpStore: { openPopUp, closePopUp },
    productsStore: { setSelectedProduct },
  } = useStore();

  const { t } = useMyTranslations<CatalogueProductsTableTransFields>([catalogueProductsTableTranslations.name]);

  const { getValues, setValue, watch } = useFormContext();

  const deleteHandler = useThrottleFn(
    useCallback((id: string) => {
      setValue(
        'products',
        getValues('products').filter((item: ICataloguesProduct) => item.id !== id)
      );
      closePopUp();
    }, [])
  );

  const onEditClick = useCallback((product: IProductData) => {
    setSelectedProduct(product);
    openPopUp({
      formName: 'addProduct',
      cb: (product: IProductData) =>
        setValue('products', getValues('products').concat({ ...product, position: DEFAULT_PRODUCT_POSITION })),
    });
  }, []);

  const columns: Column<ICataloguesProduct>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'cataloguesProductName',
        Cell: ({ row: { original } }: CellProps<ICataloguesProduct>) => {
          return (
            <div
              className={styles.avatar}
              onClick={() => {
                setValue('selectedProdId', getValues('selectedProdId') === original.id ? '' : original.id);
              }}>
              <AvatarIcon
                avatar={updateImageUrl(original.imageUrl, AVATAR_SIZE * 2)}
                name='point image'
                size={36}
                styleProps={{ borderRadius: 8 }}
              />
              {original.name ? <p className={styles.name}>{original.name}</p> : '—'}
            </div>
          );
        },
        accessor: (row) => row.name,
        width: 20,
      },
      {
        Header: t('price'),
        id: 'price',
        Cell: ({
          row: {
            original: { discountPrice, price },
          },
        }: CellProps<ICataloguesProduct>) => {
          return (
            <div className={styles.priceCell}>
              {discountPrice ? <p className={`${styles.discount}`}>{discountPrice}</p> : null}
              <p className={`${discountPrice && styles.originalPrice}`}>{price}</p>
            </div>
          );
        },
        accessor: (row) => row.discountPrice || row.price || '—',
        width: 5,
      },
      {
        Header: t('width'),
        id: 'width',
        Cell: ({ row: { original, index } }: CellProps<ICataloguesProduct>) => {
          return (
            <CataloguesProductPositionCell
              id={original.id}
              name={`products[${index}].position.width`}
              inputName={'width'}
            />
          );
        },
        accessor: (row) => row.position.width,
        width: 7,
      },
      {
        Header: t('height'),
        id: 'height',
        Cell: ({ row: { original, index } }: CellProps<ICataloguesProduct>) => {
          return (
            <CataloguesProductPositionCell
              id={original.id}
              name={`products[${index}].position.height`}
              inputName={'height'}
            />
          );
        },
        accessor: (row) => row.position.height,
        width: 7,
      },
      {
        Header: 'x',
        id: 'left',
        Cell: ({ row: { original, index } }: CellProps<ICataloguesProduct>) => {
          return (
            <CataloguesProductPositionCell
              id={original.id}
              name={`products[${index}].position.left`}
              inputName={'left'}
            />
          );
        },
        accessor: (row) => row.position.left,
        width: 5,
      },
      {
        Header: 'y',
        id: 'top',
        Cell: ({ row: { original, index } }: CellProps<ICataloguesProduct>) => {
          return (
            <CataloguesProductPositionCell
              id={original.id}
              name={`products[${index}].position.top`}
              inputName={'top'}
            />
          );
        },
        accessor: (row) => row.position.top,
        width: 5,
      },
      {
        Header: '',
        id: 'option',
        Cell: ({ row: { original } }: CellProps<ICataloguesProduct>) => (
          <div className={styles.actionBoxBtn}>
            <div
              className={styles.editBtn}
              onClick={() =>
                openPopUp({
                  title: t('confirmationTitle'),
                  description: t('confirmationDescription'),
                  buttonLabel: t('confirmationButtonLabel'),
                  formName: 'confirmation',
                  cb: () => deleteHandler(original.id),
                })
              }>
              <CloseIcon />
            </div>
            <ComponentWithTooltip
              component={
                <button className={styles.editBtn} onClick={() => onEditClick(original)}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='clone product'
              place='top'
            />
          </div>
        ),
        width: 3,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    []
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      {watch('products') && watch('products').length > 0 ? (
        <Table
          columns={columns as never}
          data={[...watch('products')]}
          isFullHeight
          selectedRows={{ [watch('selectedProdId')]: true }}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
});
